import { useEffect } from 'react'
import { Link, useRouteError } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { logger } from '@/lib/logger'
import { recoverChunkLoadError } from '@/lib/chunk-recovery'

/**
 * RouteError — the boundary that catches a thrown route, in two variants.
 *
 * Frame 21d: «RouteError er montert på tre rutetrær — app, marked og
 * innlogging — uten å vite hvilket den avbrøt. Minimumskravet: ikke tilby
 * "tilbake til dashbordet" til en utlogget besøkende.»
 *
 *   shell="app"    → «Noe gikk galt her» · «Resten av arrangementet fungerer.»
 *                    Last på nytt · Til dashbordet
 *   shell="public" → «Denne siden lastet ikke» · «Prøv igjen, eller kontakt oss.»
 *                    Last på nytt · Til forsiden
 *
 * Two corrections to the frame, both from reading the router rather than the
 * frame (`routes/index.tsx`):
 *
 * 1. It was mounted on three trees, but NOT the three the frame names. The
 *    auth tree and all eight token routes had **no boundary at all**, so a
 *    throw there was a white screen. `22c` ranks the token routes P0 for
 *    exactly this reason: «Én ekstern bruker per invitasjon — feilen er
 *    offentlig.» Both now have one, on the public variant.
 * 2. The old version printed `error.message` straight at the reader on an
 *    `nb`-first product, in English. A technical string is not an explanation
 *    — `24a`: «Ingen skyld hos brukeren, ingen "feil kode", ingen teknisk
 *    streng.» It goes to the console, where it is useful, and not to the page.
 *
 * Kept dependency-free on purpose: the seven auth pages are eager imports, so
 * anything this file pulls in lands in the initial bundle (21d).
 */
export function RouteError({ shell = 'public' }: { shell?: 'app' | 'public' }) {
  const error = useRouteError()
  const { t } = useTranslation()

  useEffect(() => {
    if (!recoverChunkLoadError(error)) logger.error('Route error:', error)
  }, [error])

  const isApp = shell === 'app'

  return (
    <div className="flex min-h-[60vh] h-full items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <AlertTriangle className="size-14 text-[var(--icon-warning)] mx-auto mb-4" aria-hidden="true" />
        <h1 className="text-h1 text-[var(--text-primary)] mb-2">
          {isApp ? t('routeError.appTitle') : t('routeError.publicTitle')}
        </h1>
        <p className="text-body text-[var(--text-secondary)] mb-6">
          {isApp ? t('routeError.appBody') : t('routeError.publicBody')}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button onClick={() => window.location.reload()}>
            <RotateCcw className="size-4" aria-hidden="true" />
            {t('routeError.reload')}
          </Button>
          {/* The whole point of the frame: never offer the dashboard to
              someone who is not signed in. */}
          <Button variant="secondary" asChild>
            <Link to={isApp ? '/dashboard' : '/'}>
              {isApp ? t('routeError.toDashboard') : t('routeError.toFrontPage')}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  )
}
